import { getItems } from '../api/figApi';
import { normalizeValue } from './normalize';
import { photoNameRe, galleryNames, nextPhotoName, sniffImageType } from './photoNames';
import { uploadPhotoFile } from './photoUpload';

export { photoNameRe, galleryNames, nextPhotoName, sniffImageType };

// ============================================================
// CONTACTS — SharePoint read + write path.
//
// ---------------------------------------------------------------------------
// WHY THIS MODULE HAS ITS OWN WRITE HELPERS
// ---------------------------------------------------------------------------
// figApi's createItem / updateItem / deleteItem all route through ensureList(),
// and ensureList drives LIST_DEFINITIONS — which is not a passive schema
// description but two write paths:
//
//   1. ensureMissingColumns() POSTs any column in the definition that is
//      missing from the live list, swallowing every error
//      (`catch { /* column sync should never block */ }`).
//   2. A list that is not found is CREATED, with def.columns on it.
//
// People and People_Channels were provisioned by
// scripts/provision_people_lists.js with asserted indexes, a unique constraint
// on PersonId, and allowTextEntry pinned on every choice column. A
// LIST_DEFINITIONS entry carries none of that and could not reproduce it, so a
// definition that drifted would quietly mutate a production schema holding
// 1,461 people. figApi's updateItem also filters payloads against that same
// definition, so with no entry it would silently drop every field.
//
// Hence: local helpers, list ids resolved READ-ONLY by displayName, and an
// explicit allowlist per list. Nothing here can create a list or a column.
//
// getItems (read) is still reused for People_Channels: it paginates via
// @odata.nextLink and is provably read-only for these lists precisely BECAUSE
// they are absent from LIST_DEFINITIONS — ensureMissingColumns returns
// immediately with no definition, and a missing list throws before any POST.
//
// People is read LOCALLY (readPeople) since 2026-09-11: getItems cannot
// $select, and People now carries ExchangeSnapshot (up to ~1 KB of JSON per
// row, 1,461 rows) and ExchangeChangeKey, which the page never uses. Selecting
// the fields it does use cut the People payload from 3.37 MB to 2.35 MB per
// load. ExchangeSyncState IS selected — it drives the sync pill.
// ============================================================

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const GRAPH = 'https://graph.microsoft.com/v1.0';

export const CONTACTS_LISTS = {
  PEOPLE: 'People',
  CHANNELS: 'People_Channels',
};

export const PHOTO_LIBRARY = 'People_Photos';

// ── Editable-field allowlists ──
//
// PersonId is the identity 1,909 channel rows join on and is unique-constrained
// in SharePoint; Source records where a row came from. Both are set once at
// creation and never again. ExchangeId belongs to Sync A. PhotoRef is written
// by setPhotoRef() ONLY, which checks the name against photoNameRe() first — a
// form must not be able to put anything in either.
//
// ExchangeSnapshot / ExchangeChangeKey / ExchangeSyncState belong to Sync A
// (~/sharepoint-mcp/people-exchange.js) and are written only under the app
// credential. They are the record of what the phone copy holds; if this page
// ever wrote a stale in-memory copy back, the next reconcile would read the
// wrong baseline and refuse a legitimate save as a phone-side edit.
// MergedInto is set by people-merge.js (sharepoint-mcp cc6dad1) on an absorbed
// row, together with ExchangeSyncState = merged. The page reads it to link the
// survivor and never writes it.
//
// PEOPLE_NEVER is documentation of intent, not the enforcement: buildFields()
// iterates the ALLOWLIST, so a key absent from PEOPLE_EDITABLE can never reach
// a PATCH no matter what object is passed in.
const PEOPLE_EDITABLE = [
  'Title', 'GivenName', 'FamilyName', 'Kind', 'Org', 'JobTitle',
  'Domain', 'Relationship', 'Context', 'Tags', 'Birthday',
  'StandingSender', 'Notes',
];
const PEOPLE_CREATE_ONLY = ['PersonId', 'Source'];
const PEOPLE_NEVER = ['ExchangeId', 'PhotoRef', 'ExchangeSnapshot', 'ExchangeChangeKey', 'ExchangeSyncState', 'MergedInto'];

// Normalized is deliberately absent: it is always recomputed from Value by
// normalize.js, never accepted from a form.
const CHANNEL_EDITABLE = ['Value', 'Label', 'IsPrimary'];
const CHANNEL_CREATE_ONLY = ['PersonId', 'Kind', 'Source'];

const str = v => (typeof v === 'string' ? v : v == null ? '' : String(v));

// ── read-only resolution ─────────────────────────────────────────────────────

let cachedSiteId = null;
const cachedListIds = {};

async function resolveSiteId(token) {
  if (cachedSiteId) return cachedSiteId;
  const res = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to resolve SharePoint site (${res.status})`);
  cachedSiteId = (await res.json()).id;
  return cachedSiteId;
}

/**
 * Resolve a list id by displayName. READ ONLY — a missing list throws rather
 * than being created. There is no code path in this module that can provision
 * anything.
 */
async function resolveListId(token, listName) {
  if (cachedListIds[listName]) return cachedListIds[listName];
  const siteId = await resolveSiteId(token);
  const res = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=200`,
    { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Failed to list SharePoint lists (${res.status})`);
  const hit = ((await res.json()).value || [])
    .find(l => l.displayName.toLowerCase() === listName.toLowerCase());
  if (!hit) throw new Error(`List "${listName}" not found. It is not created automatically.`);
  cachedListIds[listName] = hit.id;
  return hit.id;
}

async function listBase(token, listName) {
  const siteId = await resolveSiteId(token);
  const listId = await resolveListId(token, listName);
  return `${GRAPH}/sites/${siteId}/lists/${listId}`;
}

// Every People column the page reads. ExchangeSnapshot and ExchangeChangeKey
// are deliberately ABSENT — see the module header.
const PEOPLE_READ_FIELDS = [
  'PersonId', 'Title', 'GivenName', 'FamilyName', 'Kind', 'Org', 'JobTitle',
  'Domain', 'Relationship', 'Context', 'Tags', 'Birthday', 'StandingSender',
  'PhotoRef', 'Notes', 'ExchangeId', 'Source', 'ExchangeSyncState', 'MergedInto',
];
const PEOPLE_EXPAND = `$expand=fields($select=${PEOPLE_READ_FIELDS.join(',')})`;

/** All People rows, selected fields only. Paginates like getItems. Read-only. */
async function readPeople(token) {
  const base = await listBase(token, CONTACTS_LISTS.PEOPLE);
  const headers = { Authorization: `Bearer ${token}` };
  let url = `${base}/items?${PEOPLE_EXPAND}&$top=500`;
  const out = [];
  while (url) {
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Failed to load People (${res.status})`);
    const data = await res.json();
    out.push(...(data.value || []).map(i => ({ id: i.id, ...i.fields })));
    url = data['@odata.nextLink'] || null;
  }
  return out;
}

// ── write primitives ─────────────────────────────────────────────────────────

/**
 * Build a payload from an allowlist.
 *
 * CLEARING SENDS null, NEVER "". The live probe on Watch_Open (2026-08-26,
 * recorded in scripts/migrate_watch_to_sharepoint.js) established that for text
 * and number columns "", null and an omitted key all converge to ABSENT on
 * read — but sending null states the intent instead of relying on that
 * coercion, and an omitted key means "leave alone", which is a different
 * instruction entirely on a PATCH.
 */
function buildFields(source, allowlist) {
  const out = {};
  for (const key of allowlist) {
    if (!(key in source)) continue;          // absent key = leave unchanged
    const v = source[key];
    if (v === '' || v === undefined) { out[key] = null; continue; }
    out[key] = v;
  }
  return out;
}

async function graphWrite(token, url, method, body) {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = await res.json();
      msg = j?.error?.message || msg;
      const err = new Error(msg);
      err.status = res.status;
      err.code = j?.error?.code;
      throw err;
    } catch (e) {
      if (e.status) throw e;
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
  }
  return res.status === 204 ? null : res.json();
}

async function patchFields(token, listName, itemId, fields) {
  const base = await listBase(token, listName);
  return graphWrite(token, `${base}/items/${itemId}/fields`, 'PATCH', fields);
}

async function postItem(token, listName, fields) {
  const base = await listBase(token, listName);
  return graphWrite(token, `${base}/items`, 'POST', { fields });
}

async function deleteItemById(token, listName, itemId) {
  const base = await listBase(token, listName);
  return graphWrite(token, `${base}/items/${itemId}`, 'DELETE');
}

// ── shaping ──────────────────────────────────────────────────────────────────

function shapeChannel(r) {
  return {
    id: r.id,
    personId: str(r.PersonId),
    kind: str(r.Kind),
    value: str(r.Value),
    normalized: str(r.Normalized),
    label: str(r.Label),
    // IsPrimary is ABSENT rather than false on non-primary rows — the importer
    // omitted it deliberately, and 286 of 1,909 live rows have no key at all.
    // Test for true; never for falsiness.
    isPrimary: r.IsPrimary === true,
    source: str(r.Source),
  };
}

function shapePerson(r, chans) {
  const order = c => (c.isPrimary ? 0 : 1);
  const sortCh = list => list.slice()
    .sort((a, b) => order(a) - order(b) || a.label.localeCompare(b.label));
  return {
    id: r.id,
    personId: str(r.PersonId),
    title: str(r.Title),
    givenName: str(r.GivenName),
    familyName: str(r.FamilyName),
    kind: str(r.Kind),
    org: str(r.Org),
    jobTitle: str(r.JobTitle),
    domain: str(r.Domain),
    relationship: str(r.Relationship),
    context: str(r.Context),
    tags: str(r.Tags),
    birthday: str(r.Birthday),
    photoRef: str(r.PhotoRef),
    standingSender: r.StandingSender === true,
    notes: str(r.Notes),
    source: str(r.Source),
    // Outcome of the last LIVE Sync A pass ('' | ok | photo-deferred | held |
    // stop | phone-deleted | merged). held / stop / phone-deleted / merged
    // surface as a pill on load (reconcileQueue.stateFromColumn); merged rows
    // are also hidden from the list by default. See Contacts.js.
    exchangeSyncState: str(r.ExchangeSyncState),
    // READ-ONLY. PersonId of the survivor when exchangeSyncState is 'merged'
    // (set by people-merge.js), '' otherwise. Not in PEOPLE_EDITABLE, so no
    // save path can reach it; the page only renders it as a link.
    mergedInto: str(r.MergedInto),
    phones: sortCh(chans.filter(c => c.kind === 'Phone')),
    emails: sortCh(chans.filter(c => c.kind === 'Email')),
    channels: chans,
  };
}

// ── reads ────────────────────────────────────────────────────────────────────

/**
 * Read both lists and join them.
 *
 * The join key is the PersonId TEXT column, never the SharePoint item id: ids
 * are assigned per-list and are unrelated across lists (both currently start at
 * 1). PersonId is the identity the importer assigned.
 */
export async function loadDirectory(token) {
  const [peopleRows, channelRows] = await Promise.all([
    readPeople(token),
    getItems(token, CONTACTS_LISTS.CHANNELS),
  ]);
  const byPerson = new Map();
  for (const r of channelRows) {
    const c = shapeChannel(r);
    if (!c.personId) continue;
    if (!byPerson.has(c.personId)) byPerson.set(c.personId, []);
    byPerson.get(c.personId).push(c);
  }
  const people = peopleRows.map(r => shapePerson(r, byPerson.get(str(r.PersonId)) || []));
  people.sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) ||
    a.personId.localeCompare(b.personId));
  return people;
}

/**
 * Re-read ONE person and their channels straight from SharePoint.
 *
 * Every save calls this and the page renders what comes back. The page must
 * never show local form state as if it were saved: if SharePoint coerced,
 * truncated or rejected part of a write, the re-read is what reveals it.
 * Both PersonId columns are indexed, so these filters do not need the
 * HonorNonIndexedQueries header.
 */
export async function reloadPerson(token, personId) {
  const pBase = await listBase(token, CONTACTS_LISTS.PEOPLE);
  const cBase = await listBase(token, CONTACTS_LISTS.CHANNELS);
  const q = encodeURIComponent(`'${String(personId).replace(/'/g, "''")}'`);
  const headers = { Authorization: `Bearer ${token}` };
  const [pRes, cRes] = await Promise.all([
    fetch(`${pBase}/items?${PEOPLE_EXPAND}&$filter=fields/PersonId eq ${q}&$top=5`, { headers }),
    fetch(`${cBase}/items?$expand=fields&$filter=fields/PersonId eq ${q}&$top=200`, { headers }),
  ]);
  if (!pRes.ok) throw new Error(`Re-read failed for ${personId} (${pRes.status})`);
  if (!cRes.ok) throw new Error(`Re-read of channels failed for ${personId} (${cRes.status})`);
  const pRows = ((await pRes.json()).value || []).map(i => ({ id: i.id, ...i.fields }));
  const cRows = ((await cRes.json()).value || []).map(i => ({ id: i.id, ...i.fields }));
  if (pRows.length === 0) return null;
  return shapePerson(pRows[0], cRows.map(shapeChannel));
}

// ── people writes ────────────────────────────────────────────────────────────

/** Patch a person, then return what SharePoint actually holds. */
export async function savePerson(token, person, changes) {
  const fields = buildFields(changes, PEOPLE_EDITABLE);
  if (Object.keys(fields).length) {
    await patchFields(token, CONTACTS_LISTS.PEOPLE, person.id, fields);
  }
  return reloadPerson(token, person.personId);
}

/** Highest existing P-NNNNN + 1, zero-padded to 5. */
export function nextPersonId(people) {
  let max = 0;
  for (const p of people) {
    const m = /^P-(\d+)$/.exec(p.personId || '');
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `P-${String(max + 1).padStart(5, '0')}`;
}

/**
 * Create a person.
 *
 * PersonId is unique-constrained in SharePoint, so a racing tab can lose. On a
 * uniqueness rejection the max is re-read from the live list and retried ONCE —
 * re-reading rather than incrementing locally, because the winner may have
 * taken several ids. A second failure is surfaced, not retried again: at that
 * point something other than a race is wrong.
 */
export async function createPerson(token, changes, people) {
  const attempt = async personId => {
    const fields = {
      ...buildFields(changes, PEOPLE_EDITABLE),
      PersonId: personId,
      Source: 'app',
    };
    if (!fields.Title) throw new Error('A name is required.');
    await postItem(token, CONTACTS_LISTS.PEOPLE, fields);
    return personId;
  };
  const isUniqueness = e =>
    e?.status === 400 || /unique|duplicate/i.test(e?.message || '');

  let id = nextPersonId(people);
  try {
    return await reloadPerson(token, await attempt(id));
  } catch (e) {
    if (!isUniqueness(e)) throw e;
    const fresh = await readPeople(token);
    id = nextPersonId(fresh.map(r => ({ personId: str(r.PersonId) })));
    return reloadPerson(token, await attempt(id));
  }
}

// ── channel writes ───────────────────────────────────────────────────────────

/**
 * Save a channel's Value/Label. Normalized is ALWAYS recomputed from Value —
 * it is the key duplicate detection and (eventually) Watch recognition rely on,
 * and it is never typed.
 */
export async function saveChannel(token, person, channel, changes) {
  const fields = buildFields(changes, CHANNEL_EDITABLE);
  if ('Value' in fields) {
    fields.Normalized = normalizeValue(channel.kind, fields.Value);
  }
  if (Object.keys(fields).length) {
    await patchFields(token, CONTACTS_LISTS.CHANNELS, channel.id, fields);
  }
  return reloadPerson(token, person.personId);
}

export async function addChannel(token, person, { kind, value, label }) {
  const fields = {
    Title: label || kind,
    PersonId: person.personId,
    Kind: kind,
    Value: value,
    Normalized: normalizeValue(kind, value),
    Source: 'manual',
  };
  if (label) fields.Label = label;
  await postItem(token, CONTACTS_LISTS.CHANNELS, fields);
  return reloadPerson(token, person.personId);
}

export async function deleteChannel(token, person, channel) {
  await deleteItemById(token, CONTACTS_LISTS.CHANNELS, channel.id);
  return reloadPerson(token, person.personId);
}

/**
 * Make one channel the primary for its kind.
 *
 * ORDER MATTERS: the existing primary is cleared FIRST, then the new one set.
 * If the second call fails the person is left with ZERO primaries, which the
 * page renders correctly (nothing is starred) and a retry fixes. The reverse
 * order would leave TWO primaries on failure — a state the import guaranteed
 * never occurs and that nothing downstream expects.
 *
 * NULL vs FALSE on a boolean column — UNVERIFIED, so this handles both.
 * The live probe recorded in scripts/migrate_watch_to_sharepoint.js (Watch_Open,
 * 2026-08-26) established that for TEXT and NUMBER columns "", null and an
 * omitted key all read back ABSENT, and that for a BOOLEAN, `false` is stored
 * as false while an omitted key reads back absent — "booleans do not converge".
 * It did NOT test null on a boolean. So null is attempted first, because absent
 * is what the other 286 non-primary rows already look like; if Graph rejects it
 * the call falls back to false. Either result renders identically: the page
 * tests `IsPrimary === true` and treats everything else as not primary.
 */
export async function setPrimary(token, person, channel) {
  const sameKind = person.channels.filter(c => c.kind === channel.kind);
  const current = sameKind.filter(c => c.isPrimary && c.id !== channel.id);

  for (const c of current) {
    try {
      await patchFields(token, CONTACTS_LISTS.CHANNELS, c.id, { IsPrimary: null });
    } catch (e) {
      // Boolean column may refuse null — see the note above.
      await patchFields(token, CONTACTS_LISTS.CHANNELS, c.id, { IsPrimary: false });
    }
  }
  await patchFields(token, CONTACTS_LISTS.CHANNELS, channel.id, { IsPrimary: true });
  return reloadPerson(token, person.personId);
}

/** Clear the primary flag without setting another. */
export async function clearPrimary(token, person, channel) {
  try {
    await patchFields(token, CONTACTS_LISTS.CHANNELS, channel.id, { IsPrimary: null });
  } catch (e) {
    await patchFields(token, CONTACTS_LISTS.CHANNELS, channel.id, { IsPrimary: false });
  }
  return reloadPerson(token, person.personId);
}

// ── duplicate detection ──────────────────────────────────────────────────────

/**
 * Where else does this normalized value already live?
 * Same person → blocked by the caller. Another person → warned about but
 * allowed: households legitimately share a landline, and the import found a
 * 12-card household on one number.
 */
export function findNormalizedOwners(people, kind, normalized, excludeChannelId) {
  if (!normalized) return [];
  const out = [];
  for (const p of people) {
    for (const c of p.channels) {
      if (c.kind !== kind) continue;
      if (c.normalized !== normalized) continue;
      if (excludeChannelId && c.id === excludeChannelId) continue;
      out.push({ personId: p.personId, title: p.title, channelId: c.id });
    }
  }
  return out;
}

// ── photos ───────────────────────────────────────────────────────────────────
//
// PhotoRef holds a FILE NAME. Graph's thumbnail and @microsoft.graph.downloadUrl
// values are pre-authenticated and expire within about an hour, so NO Graph URL
// is ever stored — not in SharePoint, not in localStorage, not in a module
// cache that outlives the page. The library is listed once per page load and
// the map lives only in component state.
//
// Photos are decorative: a failure here must never break the directory. Every
// call below resolves to an empty map rather than throwing, and the caller logs
// to the console instead of showing a banner.

async function resolvePhotoDriveId(token) {
  const siteId = await resolveSiteId(token);
  const listId = await resolveListId(token, PHOTO_LIBRARY);
  const res = await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/drive?$select=id`,
    { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Photo library drive unavailable (${res.status})`);
  return (await res.json()).id;
}

/**
 * file name -> { thumbSmall, thumbLarge, fullUrl }.
 *
 * No $select on the children call — that annotation is dropped when $select is
 * present, which is the same reason photoApi.js omits it.
 * Returns an empty Map on any failure; never throws.
 */
export async function loadPhotoMap(token) {
  try {
    const driveId = await resolvePhotoDriveId(token);
    const out = new Map();
    let url = `${GRAPH}/drives/${driveId}/root/children?$expand=thumbnails&$top=500`;
    while (url) {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Photo listing failed (${res.status})`);
      const d = await res.json();
      for (const it of d.value || []) {
        if (!it.file) continue;
        const t = it.thumbnails?.[0] || {};
        out.set(it.name, {
          thumbSmall: t.small?.url || t.medium?.url || null,
          thumbLarge: t.large?.url || t.medium?.url || null,
          fullUrl: it['@microsoft.graph.downloadUrl'] || t.large?.url || null,
        });
      }
      url = d['@odata.nextLink'] || null;
    }
    return out;
  } catch (err) {
    // Decorative. The directory works without it.
    console.warn('[Contacts] photo library unavailable:', err.message);
    return new Map();
  }
}

/** Library file names only, fresh — the 409 retry in uploadPersonPhoto re-numbers from this. */
async function listPhotoNames(token) {
  const driveId = await resolvePhotoDriveId(token);
  const names = [];
  let url = `${GRAPH}/drives/${driveId}/root/children?$select=name,file&$top=500`;
  while (url) {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Photo listing failed (${res.status})`);
    const d = await res.json();
    for (const it of d.value || []) if (it.file) names.push(it.name);
    url = d['@odata.nextLink'] || null;
  }
  return names;
}

/**
 * Upload one photo for a person into the library root (photoUpload.js). The
 * gallery is `existingNames` (from galleryNames); the file lands as the next
 * free __N name. Sets nothing on the People row — the caller decides whether
 * it becomes main via setPhotoRef().
 * @returns {Promise<{name: string, quickXorHash: string|null}>}
 */
export async function uploadPersonPhoto(token, personId, file, existingNames) {
  const driveId = await resolvePhotoDriveId(token);
  return uploadPhotoFile(token, driveId, personId, file, existingNames || [], () => listPhotoNames(token));
}

/**
 * The ONLY writer of People.PhotoRef. PATCHes that one column and nothing
 * else: null clears it (Sync A then removes the phone photo; the file stays in
 * the library), a name must match photoNameRe(person.personId) or nothing is
 * sent. PhotoRef stays in PEOPLE_NEVER, so savePerson can never reach it.
 */
export async function setPhotoRef(token, person, nameOrNull) {
  if (!person?.id || !person?.personId) throw new Error('Save the contact before changing its photo.');
  if (nameOrNull !== null) {
    if (typeof nameOrNull !== 'string' || !photoNameRe(person.personId).test(nameOrNull)) {
      throw new Error('That file is not one of this contact\'s photos.');
    }
  }
  await patchFields(token, CONTACTS_LISTS.PEOPLE, person.id, { PhotoRef: nameOrNull });
  return reloadPerson(token, person.personId);
}

/** Two letters for the no-photo fallback. Initials only — never a full name. */
export function initialsOf(person) {
  const from = (s) => (s || '').trim().charAt(0).toUpperCase();
  const a = from(person.givenName) || from(person.title);
  const b = from(person.familyName);
  return (a + b) || a || '?';
}

// ── Sync A: phone copy ───────────────────────────────────────────────────────
//
// After a save, the page asks the Netlify relay (/api/contacts-reconcile) to
// bring the Exchange contact — the one that syncs to the phone — in line with
// what SharePoint now holds. The relay verifies the Microsoft ID token (not the
// Graph access token — same pattern as watchApi.runSweep / jarvisApi) and
// forwards {personId, dryRun} to the App Service. Nothing else crosses: the
// server reads SharePoint itself, and the response is field NAMES.
//
// CONTACTS_SYNC_LIVE: only a literal boolean false in the payload opens the
// write path, at every hop. Flipping this constant is the Part 3b switch.
export const CONTACTS_SYNC_LIVE = true;
const RECONCILE_ENDPOINT = '/api/contacts-reconcile';
const PERSON_ID_RE = /^P-\d{5}$/;

/**
 * @returns {Promise<{personId, action:'noop'|'update'|'create'|'held'|'stop', fields:string[], photo, reason?, dryRun:boolean}>}
 * Throws on any non-200 (the caller renders "phone copy failed").
 */
export async function reconcilePerson(getIdToken, personId) {
  if (!PERSON_ID_RE.test(String(personId || ''))) throw new Error('bad PersonId');
  const dryRun = CONTACTS_SYNC_LIVE === true ? false : true;
  const doFetch = async (forceRefresh) => fetch(RECONCILE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken(forceRefresh)}` },
    body: JSON.stringify({ personId, dryRun }),
  });
  let res = await doFetch(false);
  if (res.status === 401) res = await doFetch(true);   // stale cached ID token
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.upstreamStatus ? ` (upstream ${err.upstreamStatus})` : '';
    throw new Error((err.error || `reconcile failed (${res.status})`) + detail);
  }
  const out = await res.json();
  return { ...out, dryRun };
}

// ── held / deleted pill: the diff and the resolutions ────────────────────────
// Both go through Netlify relays that verify the ID token and forward with
// the static key (same shape as reconcilePerson). contacts-held is the ONE
// call whose response carries contact values: the phone/app pair per field,
// shown in the panel and nowhere else.
const HELD_ENDPOINT = '/api/contacts-held';
const RESOLVE_ENDPOINT = '/api/contacts-resolve';

async function relayFetch(getIdToken, url, init) {
  const doFetch = async forceRefresh => fetch(url, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${await getIdToken(forceRefresh)}` } });
  let res = await doFetch(false);
  if (res.status === 401) res = await doFetch(true);
  return res;
}

/** @returns {Promise<{personId, state, fields:[{field,key,phone,app}]}>} throws on non-200 */
export async function fetchHeld(getIdToken, personId) {
  if (!PERSON_ID_RE.test(String(personId || ''))) throw new Error('bad PersonId');
  const res = await relayFetch(getIdToken, `${HELD_ENDPOINT}?personId=${encodeURIComponent(personId)}`, { method: 'GET' });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `held lookup failed (${res.status})`); }
  return res.json();
}

/**
 * @param {'phone'|'app'|'restore'} choice
 * @returns {Promise<object>} the resolve result (outcome / action, newState, field NAMES)
 * Throws with .status and .body on a non-200, so a 409 can be worded by the panel.
 */
export async function resolvePerson(getIdToken, personId, choice) {
  if (!PERSON_ID_RE.test(String(personId || ''))) throw new Error('bad PersonId');
  const res = await relayFetch(getIdToken, RESOLVE_ENDPOINT, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId, choice, dryRun: false }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(body.error || `resolve failed (${res.status})`); e.status = res.status; e.body = body; throw e; }
  return body;
}

// ── delete person everywhere ─────────────────────────────────────────────────
// contacts-delete relay -> App Service: Exchange contact, channel rows, People
// row. Photos stay in the library. Always live from here: the page has shown
// its confirm by the time this is called.
const DELETE_ENDPOINT = '/api/contacts-delete';

/**
 * @returns {Promise<{personId, deleted:{contact:true|'absent', channels:number, row:true}}>}
 * Throws with .status and .body on a non-200. A 404 (row already gone) is
 * thrown too — the caller decides whether that still counts as removed.
 */
export async function deletePerson(getIdToken, personId) {
  if (!PERSON_ID_RE.test(String(personId || ''))) throw new Error('bad PersonId');
  const res = await relayFetch(getIdToken, DELETE_ENDPOINT, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId, dryRun: false }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body.upstreamStatus ? ` (upstream ${body.upstreamStatus})` : '';
    const e = new Error((body.error || `delete failed (${res.status})`) + detail); e.status = res.status; e.body = body; throw e;
  }
  return body;
}

// ── search ───────────────────────────────────────────────────────────────────

/**
 * A query that is a PersonId, with or without the "P-" prefix and leading
 * zeros: "P-00042", "p00042", "00042" and "42" all find P-00042. Whole-id only:
 * "42" is not P-00420 (a bare number still reaches the channel matcher below).
 */
const PERSONID_QUERY_RE = /^p?-?(\d{1,5})$/i;
export function matchesPersonId(personId, needle) {
  const m = PERSONID_QUERY_RE.exec(needle);
  if (!m) return false;
  const digits = str(personId).replace(/^P-/i, '');
  return digits !== '' && Number(digits) === Number(m[1]);
}

/** Case-insensitive match over name, org, notes, relationship, context, tags, PersonId and channels. */
export function matchesQuery(person, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    person.title, person.org, person.jobTitle, person.notes,
    person.relationship, person.context, person.tags,
    person.givenName, person.familyName,
  ];
  for (const h of hay) if (h && h.toLowerCase().includes(needle)) return true;
  if (matchesPersonId(person.personId, needle)) return true;
  for (const c of person.channels) {
    if (c.value.toLowerCase().includes(needle)) return true;
    // Lets "5551234" find a number stored as "(555) 123-4"
    if (c.normalized.toLowerCase().includes(needle)) return true;
  }
  return false;
}

/** Digits-only href for tel:, so display formatting never breaks the link. */
export function telHref(value) {
  const d = str(value).replace(/[^\d+]/g, '');
  return d ? `tel:${d}` : null;
}

// shapePerson is exported for tests only; the page gets people through
// loadDirectory / reloadPerson.
export { shapePerson, PEOPLE_EDITABLE, PEOPLE_CREATE_ONLY, PEOPLE_NEVER, CHANNEL_EDITABLE, CHANNEL_CREATE_ONLY };
